import { executeDetailAction } from "@/components/shared/UniversalDetailPage/actions";
import type {
  DetailAction,
  DetailActionContext,
  BaseEntity,
} from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { BulkAction } from "../GroupedBulkActions";
import { getSupabase } from "@/lib/hooks/api/utils";
import type { EmailRecipient } from "@/components/crm/messages/ClientEmailSelector";

function extractEmailRecipientsFromItems<T extends BaseEntity>(items: T[]): EmailRecipient[] {
  const deduped = new Map<string, EmailRecipient>();

  for (const item of items) {
    const row = item as Record<string, unknown>;
    const rawEmail = row.email ?? row.contact_email;
    if (typeof rawEmail !== "string") continue;

    const email = rawEmail.trim();
    if (!email) continue;

    if (!deduped.has(email)) {
      const displayName =
        (typeof row.name === "string" && row.name) ||
        (typeof row.contact_name === "string" && row.contact_name) ||
        email;
      deduped.set(email, {
        id: `email-${email}`,
        type: "email",
        email,
        name: displayName,
      });
    }
  }

  return Array.from(deduped.values());
}

async function extractCompanyContactRecipients<T extends BaseEntity>(
  items: T[],
  context: DetailActionContext<T>
): Promise<EmailRecipient[]> {
  const entityName = context.pageConfig?.entityName;
  if (entityName !== "companies") return [];

  const companyIds = Array.from(
    new Set(
      items
        .map((item) => item.id)
        .filter((value): value is string => typeof value === "string" && value.length > 0)
    )
  );

  if (!companyIds.length) return [];

  try {
    const supabase = getSupabase();
    const { data, error } = await supabase
      .from("contacts")
      .select("id, name, email")
      .in("company_id", companyIds)
      .not("email", "is", null)
      .limit(5000);

    if (error || !data?.length) {
      if (error) {
        console.error("Failed to fetch company contacts for bulk email:", error);
      }
      return [];
    }

    const deduped = new Map<string, EmailRecipient>();
    for (const contact of data) {
      const email = typeof contact.email === "string" ? contact.email.trim() : "";
      if (!email || deduped.has(email)) continue;
      deduped.set(email, {
        id: `email-${email}`,
        type: "email",
        email,
        name: contact.name ?? email,
      });
    }
    return Array.from(deduped.values());
  } catch (error) {
    console.error("Error fetching company contacts for bulk email:", error);
    return [];
  }
}

async function openBulkComposeModal<T extends BaseEntity>(
  items: T[],
  context: DetailActionContext<T>
): Promise<void> {
  const directRecipients = extractEmailRecipientsFromItems(items);
  const companyRecipients = await extractCompanyContactRecipients(items, context);

  const deduped = new Map<string, EmailRecipient>();
  [...directRecipients, ...companyRecipients].forEach((recipient) => {
    if (!deduped.has(recipient.email)) {
      deduped.set(recipient.email, recipient);
    }
  });

  const recipients = Array.from(deduped.values());
  if (!recipients.length) {
    context.onToast("No email addresses found for selected items", "warning");
    return;
  }

  context.onOpenModal("compose-message", {
    initialRecipients: recipients,
  });
}

/**
 * Generate bulk actions from row actions by reusing the same handlers
 * This eliminates code duplication by calling the same action handlers in a loop
 */
export function generateBulkActionsFromRowActions<T extends BaseEntity>(
  rowActions: DetailAction<T>[],
  actionContext: DetailActionContext<T>,
  selectedItems: T[]
): BulkAction<T>[] {
  return rowActions
    .filter((action) => action.bulkable === true && !!(action.action || action.href))
    .map((action) => {
      const label =
        typeof action.label === "function" ? action.label(selectedItems[0]) : action.label;

      return {
        id: `bulk_${action.id}`,
        label,
        icon: action.icon,
        variant: action.variant,
        onClick: async (items: T[]) => {
          if (action.action === "sendEmail") {
            await openBulkComposeModal(items, actionContext);
            return;
          }

          let successCount = 0;
          for (const item of items) {
            const succeeded = await executeDetailAction(
              action,
              { ...actionContext, item },
              { suppressDefaultSuccessToast: true }
            );
            if (succeeded) successCount += 1;
          }

          if (successCount > 0) {
            actionContext.onToast(
              `${label} completed for ${successCount} ${successCount === 1 ? "item" : "items"}`,
              "success"
            );
          }
        },
        disabled: (items: T[]) => {
          // Disable if any item would fail
          return items.some((item) => action.disabled?.(item) === true);
        },
      };
    })
    .filter(Boolean) as BulkAction<T>[];
}
